import { word } from "@modules/core/utils/i18n"
import { formatCurrency } from "@modules/currency/currency"
import { PayoutStatusWithIcon } from "@modules/financesPayout/components/payoutStatusWithIcon"
import { PayoutItemEntity } from "@modules/financesPayout/entities/payoutEntity"
import { PayoutEnumApi } from "@modules/financesPayout/gateway/payoutApi"
import { usePayoutSidebar } from "@modules/financesPayout/hooks/usePayoutSidebar"
import { useLanguage } from "@modules/language/hooks/useLanguage"
import { renderTableColumns } from "@modules/ui/components/dataTables/datatable"
import { HUText } from "@modules/ui/components/huText"
import dayjs from "dayjs"
import { Column, ColumnProps } from "primereact/column"
import { DataTable, DataTableValue } from "primereact/datatable"
import { Skeleton } from "primereact/skeleton"
import { useState } from "react"
import { DefaultTheme, useTheme } from "styled-components"

type PayoutTableProps = {
  columns?: ColumnProps[]
  size: number
  empty: {
    full?: boolean
    view: React.ReactNode
  }
  isLoading: boolean
  data: PayoutItemEntity[]
  payoutEnumApi: PayoutEnumApi
} & React.HTMLAttributes<HTMLDivElement>

type PayoutColumnKey = "amount" | "status" | "externalaccount" | "launched" | "exceptedreception"

export const PayoutTable: React.FC<PayoutTableProps> = ({ columns, size, empty, isLoading, data, payoutEnumApi }) => {
  const theme = useTheme()
  const language = useLanguage()
  const { navigateToPayoutDetails } = usePayoutSidebar()
  const diplayedInfoPayout: PayoutColumnKey[] = ["amount", "status", "externalaccount", "launched", "exceptedreception"]
  const [selectedPayout, setSelectedPayout] = useState<DataTableValue>()

  const onSelectedPayout = (e: DataTableValue) => {
    setSelectedPayout(e)
    navigateToPayoutDetails(payoutEnumApi, e.id as string, {
      replaceAllComponents: true,
      onHide: () => {
        setSelectedPayout(undefined)
        return Promise.resolve(true)
      },
    })
  }

  const headerColumn = (key: PayoutColumnKey) => {
    switch (key) {
      case "amount":
        return word("finances.payoutTable.headerColumn.amout")
      case "status":
        return word("finances.payoutTable.headerColumn.status")
      case "externalaccount":
        return word("finances.payoutTable.headerColumn.externalAccount")
      case "launched":
        return word("finances.payoutTable.headerColumn.launched")
      case "exceptedreception":
        return word("finances.payoutTable.headerColumn.exceptedReception")
    }
  }

  const valueColumn = (key: PayoutColumnKey, payout: PayoutItemEntity) => {
    switch (key) {
      case "amount":
        return (
          <HUText color={theme.blackAtheleWhiteOwner} fontStyle="TS">
            {formatCurrency(payout.amount || 0, payout.currency, language)}
          </HUText>
        )
      case "status":
        return (
          <PayoutStatusWithIcon
            payoutStatus={payout.status}
            size={24}
            color={theme.sucess}
            fontStyle="BM"
            colorText={theme.blackAtheleWhiteOwner}
          />
        )
      case "externalaccount":
        return (
          <HUText color={theme.blackAtheleWhiteOwner} fontStyle="BM">
            {payout.last4Digits && payout.bankName ? `${payout.bankName} ${payout.getFormatedDigits()}` : "- "}
          </HUText>
        )
      case "launched":
        return (
          <HUText color={theme.blackAtheleWhiteOwner} fontStyle="BM">
            {dayjs(payout.createdAt).format("lll")}
          </HUText>
        )
      case "exceptedreception":
        return (
          <HUText color={theme.blackAtheleWhiteOwner} fontStyle="BM">
            {dayjs(payout.expectedReceptionDate).format("ll")}
          </HUText>
        )
      default:
        return (
          <HUText color={theme.blackAtheleWhiteOwner} fontStyle="BM">
            {word("global.error.label")}
          </HUText>
        )
    }
  }

  if (isLoading) {
    return <Skeleton width="100%" height="45rem" className="rounded-33" />
  }

  return (
    <div className="flex flex-column w-full px-4">
      {!data || data.length <= 0 ? (
        empty.view
      ) : (
        <DataTable
          value={data as DataTableValue[]}
          dataKey="id"
          lazy
          rows={size}
          emptyMessage={empty.view}
          loading={isLoading}
          selectionMode="single"
          selection={selectedPayout}
          onSelectionChange={(e) => onSelectedPayout(e.value)}
          unstyled={undefined}
          metaKeySelection={true}
          // size="small" // TODO CHANGE SIZE FOR SCREEN SIZE
        >
          {renderTableColumns(diplayedInfoPayout, headerColumn, valueColumn, theme as DefaultTheme, language)}
          {columns?.map((columnProps, index) => <Column key={"more-column-" + index} {...columnProps} />)}
        </DataTable>
      )}
    </div>
  )
}
